import { useAppDispatch, useAppSelector } from 'app/hooks';
import TextKey from 'components/common/text-key';
import EquipmentStepRecommendations from 'components/structure-page/equipment-step-recommendations';
import PtacPreferencesCard from 'components/structure-page/ptac-preferences-card';
import { useFooterConfig } from 'contexts/footer-context';
import { setActiveTab } from 'features/ui';
import useDuctedFilterContextReset from 'hooks/use-ducted-filter-context-reset';
import useDuctedPrewarm from 'hooks/use-ducted-prewarm';
import useEnsureAnchorExistingSystem from 'hooks/use-ensure-anchor-existing-system';
import useExistingEquipmentPageVisible from 'hooks/use-existing-equipment-page-visible';
import useHybridStructureAutobuild from 'hooks/use-hybrid-structure-autobuild';
import { useSizeSkipArmed } from 'hooks/use-size-skip';
import useSystemsAwaitingFirstSave from 'hooks/use-systems-awaiting-first-save';
import { useCallback, useMemo, useState } from 'react';
import runCompatibilityCheck from 'utils/equipment-compatibility-check';
import { isPtacExistingSystem } from 'utils/equipment-preferences-complete';
import { equipmentPreferencesPreviousTab } from 'utils/existing-equipment-page-visibility';
import {
  equipmentStepProceedMessage,
  EQUIPMENT_PREFERENCES_PAGE_TITLE,
  EQUIPMENT_PREFERENCES_PAGE_TITLE_KEY
} from 'utils/existing-equipment-steps';
import {
  buildComponentsKey,
  resolveEffectiveRecommendedCodes
} from 'utils/recommendations-strip-logic';
import { resolveSystemTonnageSeed } from 'utils/system-tonnage-seed';
import { warningModal } from 'utils/ui';

export default function EquipmentPreferencesPage() {
  const dispatch = useAppDispatch();
  const {
    existingSystems,
    equipmentSaveVersion,
    recommendationSelectionsBySystem
  } = useAppSelector((state) => state.structure);
  const { pageProgress } = useAppSelector((state) => state.ui);
  const coolingBtu = useAppSelector((state) => state.project.totalBTUs.cooling);
  const showExistingEquipmentTab = useExistingEquipmentPageVisible();

  useEnsureAnchorExistingSystem();
  useDuctedPrewarm();
  useHybridStructureAutobuild();
  useDuctedFilterContextReset();

  const awaitingFirstSave = useSystemsAwaitingFirstSave();

  const [effectiveCodesBySystem, setEffectiveCodesBySystem] = useState<
    Record<string, string[]>
  >({});
  const handleRecommendationsChange = useCallback(
    (
      systemUniqueId: string,
      result: {
        matched: boolean;
        recommendedCodes: string[];
        wantOnlyCodes?: string[];
      }
    ) => {
      const effectiveCodes = resolveEffectiveRecommendedCodes(
        result.recommendedCodes,
        result.wantOnlyCodes
      );
      setEffectiveCodesBySystem((prev) => {
        const existing = prev[systemUniqueId];
        const unchanged =
          !!existing &&
          existing.length === effectiveCodes.length &&
          existing.every((code, index) => code === effectiveCodes[index]);
        return unchanged ? prev : { ...prev, [systemUniqueId]: effectiveCodes };
      });
    },
    []
  );

  const runNextFetches = useCallback(() => {
    existingSystems.forEach((system) => {
      if (isPtacExistingSystem(system)) return;
      dispatch(
        runCompatibilityCheck({
          systemUniqueId: system.uniqueId,
          keptComponentUniqueIds: system.components
            .filter((c) => !c.replacing)
            .map((c) => c.uniqueId),
          systemTypeCodes: effectiveCodesBySystem[system.uniqueId] ?? [],
          componentsKey: buildComponentsKey(
            system.components,
            equipmentSaveVersion
          ),
          tonnageSeed: resolveSystemTonnageSeed({
            system,
            record:
              recommendationSelectionsBySystem[system.uniqueId]?.tonnage ??
              null,
            coolingBtu
          })
        })
      );
    });
  }, [
    existingSystems,
    effectiveCodesBySystem,
    equipmentSaveVersion,
    recommendationSelectionsBySystem,
    coolingBtu,
    dispatch
  ]);

  const preferencesIncomplete = pageProgress.equipmentPreferences !== 100;
  const proceedMessage = equipmentStepProceedMessage({
    tab: 'equipmentPreferences',
    existingSystems
  });
  const nextDisabledReason = preferencesIncomplete ? proceedMessage : undefined;

  const handleBack = useCallback(() => {
    dispatch(
      setActiveTab(equipmentPreferencesPreviousTab(showExistingEquipmentTab))
    );
  }, [dispatch, showExistingEquipmentTab]);

  const handleNext = useCallback(() => {
    if (pageProgress.equipmentPreferences !== 100) {
      warningModal(proceedMessage);
      return;
    }
    runNextFetches();
    dispatch(setActiveTab('structure'));
  }, [
    dispatch,
    pageProgress.equipmentPreferences,
    proceedMessage,
    runNextFetches
  ]);

  const skipArmed = useSizeSkipArmed();

  const handleSkipToChooseSystems = useCallback(() => {
    if (pageProgress.equipmentPreferences !== 100) {
      warningModal(proceedMessage);
      return;
    }
    runNextFetches();
    dispatch(setActiveTab('chooseSystems'));
  }, [
    dispatch,
    pageProgress.equipmentPreferences,
    proceedMessage,
    runNextFetches
  ]);

  useFooterConfig(
    useMemo(() => {
      if (skipArmed) {
        return {
          onBack: handleBack,
          onNext: handleSkipToChooseSystems,
          backText: 'Back',
          nextText: 'See Matching Systems',
          nextDisabled: preferencesIncomplete,
          nextDisabledReason,
          nextVariant: 'green' as const,
          showBackArrow: true,
          secondaryButton: {
            text: 'Get a Detailed Sizing Estimate',
            onClick: handleNext
          }
        };
      }
      return {
        onBack: handleBack,
        onNext: handleNext,
        backText: 'Back',
        nextText: 'Next Section',
        nextDisabled: preferencesIncomplete,
        nextDisabledReason,
        nextVariant: 'green' as const,
        showBackArrow: true
      };
    }, [
      skipArmed,
      handleBack,
      handleNext,
      handleSkipToChooseSystems,
      preferencesIncomplete,
      nextDisabledReason
    ])
  );

  const renderSystemStep = (system: (typeof existingSystems)[number]) => {
    if (isPtacExistingSystem(system)) {
      return <PtacPreferencesCard systemUniqueId={system.uniqueId} />;
    }
    return (
      <EquipmentStepRecommendations
        system={system}
        visibleSection='want'
        onRecommendationsChange={handleRecommendationsChange}
      />
    );
  };

  const stripHosts = existingSystems.map((system) => (
    <div key={system.uniqueId}>
      {existingSystems.length > 1 && (
        <div className='mb-1 text-sm font-semibold text-gray-700'>
          {system.name}
        </div>
      )}
      {awaitingFirstSave(system.uniqueId) ? (
        <div className='mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500'>
          Preparing recommendations…
        </div>
      ) : (
        renderSystemStep(system)
      )}
    </div>
  ));

  return (
    <div className='mx-auto flex h-full max-w-[1700px] flex-col'>
      <h2 className='w-full pb-8 pt-5 text-center text-4xl font-semibold text-gray-900'>
        <TextKey
          code={EQUIPMENT_PREFERENCES_PAGE_TITLE_KEY}
          fallback={EQUIPMENT_PREFERENCES_PAGE_TITLE}
        />
      </h2>
      <div className='flex min-h-0 grow flex-col gap-5'>
        <div className='flex w-full flex-col gap-4 pb-5'>{stripHosts}</div>
      </div>
    </div>
  );
}
